/**
 * Session-scoped counterpart to useRefetchOnFocus, for screens that fully
 * unmount and remount on every focus change instead of staying mounted
 * across a stack push/pop.
 *
 * useRefetchOnFocus tracks "have I seen a focus before" with a component-
 * instance useRef, which assumes the instance survives from one focus to
 * the next (true for stack-nav screens like TeamMembers.web.tsx). That
 * assumption breaks for screens rendered inside a tab panel that only
 * mounts its content while focused (e.g. FocusedTabContent in
 * MyAccount.web.tsx): every tab switch back is a brand-new instance, so
 * the "skip the first focus" check never sees a second focus to compare
 * against, and useRefetchOnFocus becomes a permanent no-op there.
 *
 * TA-10450 follow-up: discovered when useRefetchOnFocus was added to
 * AccountTeams.web.tsx (a cache-first screen inside exactly this kind of
 * tab panel) and a live browser check showed switching tabs away and back
 * never fired a new listTeams request — the hook never got a chance to run
 * its "subsequent focus" branch.
 *
 * This hook moves the freshness clock out of the component instance into
 * module scope, keyed by a caller-supplied string, so it survives the
 * unmount/remount cycle: the first mount for a given key in this session
 * is treated as already-fresh (the underlying cache-first query handles
 * that load), and only a later mount past the staleness window triggers an
 * explicit refetch.
 *
 * Usage:
 *   const { refetch } = useListXQuery({ fetchPolicy: 'cache-first' });
 *   useRefetchOnRemount('screen-key', refetch);
 */

import { useEffect, useRef } from 'react';

const lastRefetchedAtByKey = new Map<string, number>();
const inFlightKeys = new Set<string>();

export const useRefetchOnRemount = (
  key: string,
  refetch: () => Promise<unknown>,
  stalenessMs = 30_000
): void => {
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (!lastRefetchedAtByKey.has(key)) {
      lastRefetchedAtByKey.set(key, Date.now());
      return;
    }

    const last = lastRefetchedAtByKey.get(key) ?? 0;
    if (Date.now() - last < stalenessMs) return;
    if (inFlightKeys.has(key)) return;

    inFlightKeys.add(key);
    // Promise.resolve().then(...) — not a direct refetchRef.current() call —
    // so a synchronous throw or a non-Promise return from refetch still
    // flows through .catch()/.finally() instead of escaping the effect.
    Promise.resolve()
      .then(() => refetchRef.current())
      .then(() => {
        lastRefetchedAtByKey.set(key, Date.now());
      })
      .catch(() => undefined)
      .finally(() => {
        inFlightKeys.delete(key);
      });
  }, [key, stalenessMs]);
};
